"use client";

import { useLocale } from "next-intl";
import { useMemo } from "react";
import { z } from "zod/v4";

import { FundingEligibilityConfetti } from "@/components/home/funding-eligibility-confetti";
import {
  FundingEligibilityProvider,
  useFundingEligibility,
  useFundingEligibilityIneligibleVisible,
  useFundingEligibilityStepThreeGradient,
} from "@/components/home/funding-eligibility-context";
import { Link } from "@/components/ui/link";
import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";
import type { LiveHomeCopy } from "@/content/pages/home/get-live-home-copy";
import { cn } from "@/lib/utils";
import { evaluateFundingEligibility } from "@/lib/webmcp/funding-eligibility";
import { jsonToolResult } from "@/lib/webmcp/tool-result";
import { useWebMCPTool } from "@/lib/webmcp/use-webmcp-tool";

import { FundingEligibilityCounterCard } from "./funding-eligibility-counter-card";
import { FundingEligibilityStepCard } from "./funding-eligibility-step-card";
import { FundingEligibilitySubmitCard } from "./funding-eligibility-submit-card";

const GREY_500 = "text-[#7b8084]";

const BODY =
  "text-[14px] leading-[1.5] tracking-[-0.28px] lg:text-[16px] lg:tracking-[-0.02em]";

type FundingEligibilityCopy = LiveHomeCopy["fundingEligibility"];

export function FundingEligibility({
  copy = enLiveHomeCopy.fundingEligibility,
  className,
  webMCP = false,
}: {
  copy?: FundingEligibilityCopy;
  className?: string;
  webMCP?: boolean;
}) {
  return (
    <FundingEligibilityProvider>
      <FundingEligibilityContent
        copy={copy}
        className={className}
        webMCP={webMCP}
      />
    </FundingEligibilityProvider>
  );
}

function FundingEligibilityContent({
  copy,
  className,
  webMCP,
}: {
  copy: FundingEligibilityCopy;
  className?: string;
  webMCP: boolean;
}) {
  const locale = useLocale();
  const { confettiBurst, setApplicationEmail, setFormValues } =
    useFundingEligibility();
  const categories = useMemo(
    () =>
      copy.categoryGroups.flatMap((group) =>
        group.options.map(({ value }) => value)
      ),
    [copy.categoryGroups]
  );
  const countries = useMemo(
    () => copy.countries.map(({ value }) => value),
    [copy.countries]
  );
  const eligibilityInput = useMemo(
    () =>
      z
        .object({
          monthlyRevenue: z.number().positive(),
          category: z.string().refine((value) => categories.includes(value), {
            message: "Unsupported business category",
          }),
          country: z.string().refine((value) => countries.includes(value), {
            message: "Unsupported country",
          }),
        })
        .strict(),
    [categories, countries]
  );
  const emailInput = useMemo(
    () => z.object({ email: z.string().email() }).strict(),
    []
  );

  const eligibilityTool = useMemo(
    () => ({
      name: "check_funding_eligibility",
      description:
        "Check potential Wayflyer funding eligibility and update the homepage application-process estimate.",
      inputSchema: z.toJSONSchema(eligibilityInput),
      async execute(input: Record<string, unknown>) {
        const parsed = eligibilityInput.parse(input);
        const evaluation = evaluateFundingEligibility(parsed, locale, copy);
        setFormValues(evaluation.formValues);
        const { formValues: _formValues, ...result } = evaluation;
        return jsonToolResult(result);
      },
    }),
    [copy, eligibilityInput, locale, setFormValues]
  );
  const emailTool = useMemo(
    () => ({
      name: "prefill_application_email",
      description:
        "Prefill the homepage application email field. This never submits the form; the user must click Apply now.",
      inputSchema: z.toJSONSchema(emailInput),
      execute(input: Record<string, unknown>) {
        const { email } = emailInput.parse(input);
        setApplicationEmail(email);
        return jsonToolResult({ prefilled: true });
      },
    }),
    [emailInput, setApplicationEmail]
  );

  useWebMCPTool(eligibilityTool, webMCP);
  useWebMCPTool(emailTool, webMCP);

  return (
    <div
      className={cn(
        "relative -mx-6 -my-8 flex flex-col gap-5 overflow-visible px-6 py-8 md:-m-10 md:p-10 lg:-m-16 lg:p-16",
        className
      )}
    >
      {confettiBurst > 0 ? (
        <FundingEligibilityConfetti key={confettiBurst} />
      ) : null}

      <h2 className="text-grey-950 text-[24px] leading-[1.2] tracking-[-0.04em] lg:text-[32px]">
        {copy.headingLead}{" "}
        <span className={GREY_500}>{copy.headingEmphasis}</span>
      </h2>

      <div className="relative flex flex-col items-stretch gap-5 overflow-visible xl:flex-row xl:items-stretch">
        <FundingEligibilityCounterCard
          copy={copy}
          className="min-w-0 xl:min-h-0 xl:flex-1"
        />
        <div className="flex min-h-0 shrink-0 flex-col gap-5 lg:flex-row lg:items-stretch xl:w-[400px] xl:shrink-0 xl:flex-col">
          <FundingEligibilitySubmitCard
            copy={copy}
            className="min-w-0 flex-1 xl:flex-none"
          />
          <ApprovedCard copy={copy} className="min-w-0 flex-1 xl:flex-none" />
        </div>
      </div>
    </div>
  );
}

function ApprovedCard({
  copy,
  className,
}: {
  copy: FundingEligibilityCopy;
  className?: string;
}) {
  const showIneligibleUi = useFundingEligibilityIneligibleVisible();
  const showStepThreeGradient = useFundingEligibilityStepThreeGradient();

  return (
    <FundingEligibilityStepCard
      showGradient={showStepThreeGradient}
      className={className}
      contentClassName="flex min-h-full flex-1 flex-col gap-4 p-4 lg:p-6"
    >
      <span className="border-grey-100 flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white lg:size-16 lg:rounded-2xl">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/home/eligibility-badge-check.svg"
          alt=""
          className="size-6"
        />
      </span>
      <div className="flex flex-col gap-4">
        <StepTitle
          index="03"
          title={
            showIneligibleUi ? copy.step03TitleIneligible : copy.step03Title
          }
        />
        <p className={cn(BODY, GREY_500)}>
          {showIneligibleUi ? (
            <>
              {copy.step03BodyIneligiblePrefix}
              <Link
                href={copy.eligibilityCriteriaLinkHref}
                external={copy.eligibilityCriteriaLinkHref.startsWith("http")}
                className="text-grey-950 underline decoration-solid underline-offset-[3px]"
              >
                {copy.eligibilityCriteriaLinkText}
              </Link>
              {copy.step03BodyIneligibleSuffix}
            </>
          ) : (
            copy.step03Body
          )}
        </p>
      </div>
    </FundingEligibilityStepCard>
  );
}

function StepTitle({ index, title }: { index: string; title: string }) {
  return (
    <p className="text-grey-950 text-[18px] leading-[1.3] tracking-[-0.36px] lg:text-[20px] lg:tracking-[-0.04em]">
      <span className={GREY_500}>{index}. </span>
      {title}
    </p>
  );
}
